import { useState } from 'react';
import type { Employee } from '@shared/types';
import { terminationInput } from '@shared/schemas/termination.js';
import { useTerminateEmployee } from '../api.js';
import { Modal } from '../../../shell/Modal.js';
import { Input, Textarea } from '../../../shell/Field.js';
import { Button } from '../../../shell/Button.js';
import { toast } from '../../../shell/Toast.js';
import { ApiRequestError } from '../../../lib/api.js';

export function TerminationModal({ employee, signerName, onClose }: {
  employee: Employee; signerName: string; onClose: () => void;
}) {
  const mutation = useTerminateEmployee();
  const today = new Date().toLocaleDateString('en-CA');
  const [form, setForm] = useState({
    noticeDate: today, effectiveDate: today, reason: '', propertyReturn: '',
    finalCompensation: '', additionalNotes: '', signerName, signerTitle: '', confirmed: false,
  });
  const [error, setError] = useState('');
  const change = (key: keyof typeof form, value: string | boolean) => setForm((current) => ({ ...current, [key]: value }));
  const close = () => { if (!mutation.isPending) onClose(); };
  return (
    <Modal open onClose={close} title="Notice of termination" size="lg">
      <form className="space-y-4" onSubmit={async (event) => {
        event.preventDefault();
        if (mutation.isPending) return;
        const parsed = terminationInput.safeParse(form);
        if (!parsed.success) { setError(parsed.error.issues[0]?.message || 'Complete all required fields.'); return; }
        setError('');
        try {
          await mutation.mutateAsync({ id: employee.id, body: parsed.data });
          toast('success', 'Termination notice saved to the employee file. Status changed to Terminated.');
          onClose();
        } catch (err) {
          setError(err instanceof ApiRequestError ? err.body.message : 'Unable to save termination notice. Please try again.');
        }
      }}>
        <p className="text-sm text-[var(--color-muted)]">Complete the notice for {employee.legalName || employee.name}. The PDF is confidential and available to administrators in the employee’s Documents section.</p>
        <fieldset disabled={mutation.isPending} className="space-y-4">
          <Input label="Employee name" value={employee.legalName || employee.name} readOnly />
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Notice date" type="date" required value={form.noticeDate} onChange={(e) => change('noticeDate', e.target.value)} />
            <Input label="Termination effective date" type="date" required value={form.effectiveDate} onChange={(e) => change('effectiveDate', e.target.value)} />
          </div>
          <Textarea label="Reason for termination" required maxLength={4000} rows={3} value={form.reason} onChange={(e) => change('reason', e.target.value)} />
          <Textarea label="Company property return / management receipt" required maxLength={4000} rows={3} placeholder="List returned property, confirmation of receipt, or items still outstanding." value={form.propertyReturn} onChange={(e) => change('propertyReturn', e.target.value)} />
          <Textarea label="Final compensation arrangements" required maxLength={4000} rows={3} placeholder="Accrued wages, unused PTO, applicable benefits, and payment arrangements." value={form.finalCompensation} onChange={(e) => change('finalCompensation', e.target.value)} />
          <Textarea label="Additional terms & notes (optional)" maxLength={4000} rows={3} value={form.additionalNotes} onChange={(e) => change('additionalNotes', e.target.value)} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Issuer name" required maxLength={120} value={form.signerName} onChange={(e) => change('signerName', e.target.value)} />
            <Input label="Issuer title" required maxLength={120} value={form.signerTitle} onChange={(e) => change('signerTitle', e.target.value)} />
          </div>
          <p className="text-sm">The PDF includes a signature line for the issuer; no signature is applied automatically.</p>
          <label className="flex items-start gap-3 text-sm">
            <input type="checkbox" required checked={form.confirmed} onChange={(e) => change('confirmed', e.target.checked)} className="mt-1" />
            <span>I confirm this notice is ready to file. Saving immediately changes this employee’s status to Terminated and revokes login access, even if the effective date is in the future.</span>
          </label>
        </fieldset>
        {error && <p role="alert" className="text-sm text-[var(--color-danger)]">{error}</p>}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" onClick={close} disabled={mutation.isPending}>Cancel</Button>
          <Button type="submit" variant="danger" loading={mutation.isPending}>Save notice & terminate</Button>
        </div>
      </form>
    </Modal>
  );
}
